<div
  class="checkout-container"
  [ngClass]="{'full-height-on-mobile': quickCheckoutState !== 'form'}"
  data-test="quick-checkout-container"
  >
  <ng-switch [ngSwitch]="quickCheckoutState">
    <ng-container *ngSwitchCase="'form'" [ngTemplateOutlet]="form"></ng-container>
    <ng-container *ngSwitchCase="'error'" [ngTemplateOutlet]="submissionError"></ng-container>
    <ng-container *ngSwitchCase="'submitted'" [ngTemplateOutlet]="submissionConfirmation"></ng-container>
    <ng-container *ngSwitchCase="'select-new-time'" [ngTemplateOutlet]="calendar"></ng-container>
    <ng-container *ngSwitchCase="'loading'" [ngTemplateOutlet]="loading"></ng-container>
  </ng-switch>

  <ng-template #form>
    <div class="form-header" data-test="checkout-header">
      <h2> Create your account to continue </h2>
      <span> Create a free, secure account to book sessions or message {{provider.firstName}}.</span>
    </div>
    <div
      *ngIf="requestedSessionPreferences.sessionType"
      class="requested-session-info" data-test="requested-session-details"
    >
      <span [ngSwitch]="requestedSessionPreferences.sessionType" class="session-preference session-type">
        <ng-container *ngSwitchCase="sessionTypePreference.IN_PERSON">
          <iris-icon name="location_on" [size]="iconSizes.SM" colorToken="$color-dec-kelly-100"></iris-icon>
          In-person session
        </ng-container>
        <ng-container *ngSwitchCase="sessionTypePreference.VIDEO">
          <iris-icon name="videocam" [size]="iconSizes.SM" colorToken="$color-dec-kelly-100"></iris-icon>
          Video session
        </ng-container>
        <ng-container *ngSwitchDefault>
          <iris-icon name="videocam" [size]="iconSizes.SM" colorToken="$color-dec-kelly-100"></iris-icon>
          Video or in-person session
        </ng-container>
      </span>
      <span *ngIf="requestedSessionPreferences.startTime" class="session-preference session-time">
        <iris-icon
          name="access_time"
          [size]="iconSizes.SM"
          colorToken="$color-dec-kelly-100"
        ></iris-icon>
        {{ requestedSessionPreferences.startTime | formatRequestedTimeSlot:provider.timeZone }}
      </span>
    </div>

    <form [formGroup]="quickCheckoutFormGroup" class="checkout-form">
      <ng-container *ngIf="isMobile$ | async; else cardView">
        <section class="mobile-checkout-section location-and-payment">
          <ng-container *ngTemplateOutlet="locationAndPayment; context: { $implicit: quickCheckoutFormGroup }">
          </ng-container>
        </section>
        <section class="mobile-checkout-section help-activate-coverage" *ngIf="quickCheckoutFormGroup?.controls?.activeDuty?.value === true">
          <ng-container *ngTemplateOutlet="helpActivateCoverage">
          </ng-container>
        </section>
        <section class="mobile-checkout-section contact-info">
          <ng-container *ngTemplateOutlet="contactInfo; context: { $implicit: quickCheckoutFormGroup }">
          </ng-container>
        </section>
        <section class="mobile-checkout-section contact-preferences">
          <ng-container *ngTemplateOutlet="contactPreferences; context: { $implicit: quickCheckoutFormGroup }">
          </ng-container>
        </section>
        <section class="mobile-checkout-section heard-from">
          <ng-container *ngTemplateOutlet="heardFrom; context: { $implicit: quickCheckoutFormGroup }">
          </ng-container>
        </section>
      </ng-container>
      
      <ng-template #cardView>
        <iris-card>
          <ng-container *ngTemplateOutlet="locationAndPayment; context: { $implicit: quickCheckoutFormGroup }">
          </ng-container>
        </iris-card>

        <iris-card class="help-activate-card" *ngIf="quickCheckoutFormGroup?.controls?.activeDuty?.value === true">
          <ng-container *ngTemplateOutlet="helpActivateCoverage; context: { $implicit: quickCheckoutFormGroup }">
          </ng-container>
        </iris-card>
  
        <iris-card>
          <ng-container *ngTemplateOutlet="contactInfo; context: { $implicit: quickCheckoutFormGroup }">
          </ng-container>
        </iris-card>
  
        <iris-card>
          <ng-container *ngTemplateOutlet="contactPreferences; context: { $implicit: quickCheckoutFormGroup }">
          </ng-container>
        </iris-card>

        <iris-card>
          <ng-container *ngTemplateOutlet="heardFrom; context: { $implicit: quickCheckoutFormGroup }">
          </ng-container>
        </iris-card>
      </ng-template>
  
      <iris-button-container
        [alignment]="irisButtonAlignment.START"
        [mobileLayout]="irisButtonContainerLayout"
        [desktopLayout]="irisButtonContainerDesktopLayout"
      >
        <iris-button 
          (click)="onCancel()" 
          [type]="irisButtonTypeSecondary" 
          data-test="cancel-button"
        >
          Cancel
        </iris-button>
        <iris-button 
          class="submit-checkout-button"
          [disabled]="!quickCheckoutFormGroup.valid || isAnyFieldDisabled"
          (click)="onClickSubmit(requestedSessionPreferences)"
          data-test="create-account-button"
        >
          {{ useAuth0 ? 'Next' : 'Create my account!' }}
        </iris-button>
      </iris-button-container>
    </form>
  </ng-template>

  <ng-template #submissionError>
    <div class="error-state">
      <iris-empty-state
        title="There was an error when submitting your request."
        details="Please try again."
        data-test="error-state"
      />
      <iris-button-container [alignment]="irisButtonAlignment.END">
        <iris-button (click)="onBack()" [type]="irisButtonTypeSecondary">
          Back to form
        </iris-button>
        <iris-button (click)="onCancel()">
          Close
        </iris-button>
      </iris-button-container>
    </div>
  </ng-template>

  <ng-template #submissionConfirmation>
    <div class="submitted-message" data-test="submitted-message">
      <iris-empty-state
        title="Click on the link we’ve just sent you to log in to your account."
        details="In the next few minutes you’ll receive an email from SonderMind Support with a temporary secure link to create your password and log in."
        imageSrc="/assets/email.svg"
        data-test="confirmation-modal"
      />
      <div
        *ngIf="requestedSessionPreferences.sessionType"
        class="requested-timeslot" data-test="requested-timeslot"
      >
      <p class="confirmation-message-bold" *ngIf="requestedSessionPreferences.startTime">
        {{provider.firstName}} will review and confirm your session after you log in.
      </p>

      <p *ngIf="!requestedSessionPreferences.startTime">
        <b class="confirmation-message-bold">You can send a message to or request a session with {{provider.firstName}} after you log in.</b>
        They may also reach out to you to get more information about your therapy needs.
      </p>

        <span *ngIf="requestedSessionPreferences.sessionType === sessionTypePreference.VIDEO">
          <iris-icon
            name="videocam"
            [size]="iconSizes.SM"
            colorToken="$color-dec-kelly-100"
          />
          Video session
        </span>
        <span *ngIf="requestedSessionPreferences.sessionType === sessionTypePreference.IN_PERSON">
          <iris-icon
            name="location_on"
            [size]="iconSizes.SM"
            colorToken="$color-dec-kelly-100"
          /> 
          In-person session
        </span>
        <span *ngIf="requestedSessionPreferences.sessionType === sessionTypePreference.NO_PREFERENCE">
          <iris-icon
            name="videocam"
            [size]="iconSizes.SM"
            colorToken="$color-dec-kelly-100"
          /> 
          Video or in-person session
        </span>

        <span class="appointment-time" *ngIf="requestedSessionPreferences.startTime">
          <iris-icon
            name="access_time"
            [size]="iconSizes.SM"
            colorToken="$color-dec-kelly-100"
          /> 
          {{ requestedSessionPreferences.startTime | formatRequestedTimeSlot:provider.timeZone }}
        </span>
      </div>
      <p class="resend-support-message" data-test="resend-support-message">
        If you don’t receive something from us, hang tight! It could take a few minutes to arrive.
        You can also check your spam folder, try
        <a (click)="onClickResendEmail()" class="resend-email-link">resending the email</a>,
        or contact the support team at
        <a
          (click)="onClickSupportLink()"
          class="support-link"
          href="https://www.sondermind.com/contact-us/"
          target="_blank"
        >sondermind.com/contact-us</a>.
      </p>
    </div>
  </ng-template>
</div>

<ng-template #loading>
  <div class="loader"></div>
</ng-template>

<ng-template #calendar>
  <!-- break inserted into the header text to separate the two lines -->
  <public-provider-calendar
    [appointmentSlots]="appointmentSlots"
    (requestASessionClicked)="onClickSubmit($event)"
    headerText="Please select a new time slot. <br/> Your original appointment is no longer available."
    [isInModal]="true"
    [providerId]="provider.id"
    [providerPrimaryAddress]="provider.primaryAddress"
    [providerSessionTypesOffered]="provider.sessionTypesOffered"
    [providerTimeZone]="provider.timeZone"
  ></public-provider-calendar>
</ng-template>

<ng-template #locationAndPayment let-formGroup>
  <div [formGroup]="formGroup" class="question-group">
    <div class="question-with-check" id="location-question">
      <iris-form-field 
        label="Your location" 
        helperText="The state where you intend to receive care." 
        class="checkout-text-label"
        data-test="location-label"
      >
        <iris-dropdown
          formControlName="location"
          [options]="stateOptions"
          placeholder="Select a state"
          data-test="location-dropdown"
        ></iris-dropdown>
      </iris-form-field>
      <iris-icon
        [ngClass]="{'show-icon': quickCheckoutFormGroup.get('location')?.valid}"
        name="check_circle"
        [size]="iconSizes.SM"
        colorToken="$color-dec-kelly-100"
        data-test="check-mark-location"
      />
    </div>

    <div 
      class="not-licensed-in-state" 
      *ngIf="showNotLicensedInStateMessage"
      data-test="not-licensed-state"
    >
      <p>{{ isNotLicensedThereMessage$ | async }}</p>

      <iris-button
        class="directory-link"
        [type]="irisButtonTypeSecondary"
        [linkHref]="linkToProviderDirectory"
        [target]="irisButtonTargetBlank"
        (click)="onClickLinkToProviderDirectory(quickCheckoutDirectoryLinkButtons.STATE)"
        data-test="show-me-providers-who-are-link"
      >
        Show me providers who are
      </iris-button>
    </div>

    <div class="question-with-check" id="payment-type-question">
      <iris-form-field 
        label="How you plan to pay for therapy" 
        helperText="The provider accepts these payment methods." 
        class="checkout-text-label"
        data-test="insurance-label"
      >
        <iris-dropdown
          formControlName="paymentType"
          [options]="availablePaymentTypes"
          placeholder="Select a payment method"
          data-test="insurance-dropdown"
        ></iris-dropdown>
      </iris-form-field>
      <iris-icon
        [ngClass]="{'show-icon': quickCheckoutFormGroup.get('paymentType')?.valid}"
        name="check_circle"
        [size]="iconSizes.SM"
        colorToken="$color-dec-kelly-100"
        data-test="check-mark-payment"
      />
    </div>

    <div class="question-with-check" id="insurance-question" *ngIf="showInsurancePlans">
      <iris-form-field 
        [label]="insuranceFormLabel" 
        helperText="The provider accepts these plans." 
        class="checkout-text-label"
        data-test="insurance-label"
      >
        <iris-dropdown
          #insuranceDropdown
          class="insurance-dropdown"
          formControlName="insurance"
          [options]="acceptedInsurances"
          placeholder="Select a plan"
          data-test="insurance-option"
        ></iris-dropdown>
      </iris-form-field>
      <iris-icon
        [ngClass]="{'show-icon': quickCheckoutFormGroup.get('insurance')?.valid}"
        name="check_circle"
        [size]="iconSizes.SM"
        colorToken="$color-dec-kelly-100"
        data-test="check-mark-insurance"
      />
    </div>

    <div class="question-with-check" id="tricare-status" *ngIf="showTricareStatus">
      <iris-form-field 
        label="Your TriCare status" 
        class="checkout-text-label"
        data-test="tricare-label"
      >
        <iris-dropdown
          class="tricare-dropdown"
          formControlName="activeDuty"
          [options]="tricareOptions"
          placeholder="Select option"
          data-test="tricare-option"
        ></iris-dropdown>
      </iris-form-field>
      <iris-icon
        [ngClass]="{'show-icon': quickCheckoutFormGroup.get('activeDuty')?.valid}"
        name="check_circle"
        [size]="iconSizes.SM"
        colorToken="$color-dec-kelly-100"
        data-test="check-mark-insurance"
      />
    </div>

    <div 
      class="other-insurance-options" 
      *ngIf="showInsurancePlans"
      data-test="other-insurance-options"
    >
      <button 
        class="other-insurance-options-button"
        (click)="toggleShowAllInsuranceOptions()"
      >
        <iris-icon
          name="chevron_right"
          [ngClass]="{'section-open': showAllInsuranceOptions}"
          [size]="iconSizes.MD"
        />
        Don't see your insurance plan listed?
      </button>
      <div class="insurance-options-dropdown" *ngIf="showAllInsuranceOptions">
        <span>
          This provider may not accept your plan. Try selecting it from the list below, and we’ll show you providers who accept that plan.
        </span>

        <iris-form-field [showOptionalTag]="false" id="all-insurances-dropdown">
          <iris-dropdown
            [formControl]="allAcceptedInsurancesControl"
            [options]="allInsuranceOptions"
            [placeholder]="'Plans SonderMind accepts in ' + quickCheckoutFormGroup.controls.location.value"
            data-test="insurance-option"
          ></iris-dropdown>
        </iris-form-field>

        <iris-button
          class="directory-link"
          [disabled]="!allAcceptedInsurancesControl.value"
          [type]="irisButtonTypeSecondary"
          [linkHref]="linkToProviderDirectory"
          [target]="irisButtonTargetBlank"
          (click)="onClickLinkToProviderDirectory(quickCheckoutDirectoryLinkButtons.INSURANCE)"
          data-test="accept-plan-providers"
        >
          Show me providers who accept this plan
        </iris-button>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #helpActivateCoverage>
  <div *ngIf="quickCheckoutFormGroup.controls.activeDuty.value === true" class="activate-coverage-container">
    <div class="header-container">
      <img
        alt="heart hands"
        src="/assets/heart-hands.svg"
        class="coverage-image"
      >
      <h2>We'll help you activate your coverage</h2>
      <p>As an active duty member, your care requires specific authorization details. To ensure everything is set up correctly and covered, our Care Coordination team will personally assist you.</p>
    </div>

    <div class="reach-out-container">
      <span>Reach out to our care coordination team to get setup</span>
      <h4>
        <iris-icon name="call"></iris-icon>
        844-THERAPY
      </h4>
    </div>
  </div>
</ng-template>

<ng-template #contactInfo let-formGroup>
  <div [formGroup]="formGroup" class="question-group">
    <div data-test="therapy-recipient">
      <h4>Who's getting therapy?</h4>
      <span>This could be you or someone else, like a family member.</span>
    </div>

    <iris-form-field 
      label="First name" 
      class="checkout-text-label" 
      data-test="first-name-label"
    >
      <iris-text-input 
        formControlName="firstName"
        autocomplete="given-name"
        data-test="first-name"
      ></iris-text-input>
    </iris-form-field>

    <iris-form-field 
      label="Last name" 
      class="checkout-text-label" 
      data-test="last-name-label"
    >
      <iris-text-input 
        formControlName="lastName"
        autocomplete="family-name"
        data-test="last-name"
      ></iris-text-input>
    </iris-form-field>

    <iris-form-field
      *ngIf="!useAuth0"
      label="Email"
      class="checkout-text-label"
      data-test="email-label"
      [customErrorMessages]="{
        emailFormat: 'Please enter a valid email address.'
      }"
    >
      <iris-text-input 
        formControlName="email"
        placeholder="The email you want associated with this account"
        data-test="email"
      ></iris-text-input>
    </iris-form-field>

    <iris-form-field 
      label="Date of birth"
      class="checkout-text-label"
      data-test="date-of-birth-label"
      [customErrorMessages]="{
        dateFormat: 'Dates must be in correct format of MM/DD/YYYY Month/Day/Year.' 
      }"
    >
      <iris-text-input 
        formControlName="birthDate"
        autocomplete="bday"
        placeholder="mm/dd/yyyy"
        [mask]="masks.birthdayMask"
        [dropSpecialCharacters]="false"
        data-test="date-of-birth"
      ></iris-text-input>
    </iris-form-field>

    <iris-form-field
      label="Mobile phone"
      data-test="phone-number-label"
      [customErrorMessages]="{
        phoneNumberFormat: phoneErrorMessage,
        invalidPhoneNumber: phoneErrorMessage
      }"
    >
      <iris-text-input 
        formControlName="phone"
        placeholder="(___) ___-____"
        [mask]="masks.phoneMask"
        data-test="phone-number"
      ></iris-text-input>
    </iris-form-field>

    <iris-checkbox 
      formControlName="contactSmsAllowed" 
      class="contact-sms-checkbox"
      data-test="sms-checkbox"
    >
      Yes, SMS text message any session reminders and account updates (standard messaging rates apply)
    </iris-checkbox>
  </div>
</ng-template>

<ng-template #contactPreferences let-formGroup>
  <div [formGroup]="formGroup" class="contact-preferences-form">
    <h4 data-test="contact-preference">How should your provider contact you?</h4>
    <iris-form-field>
      <div formGroupName="commPrefs">
        <iris-checkbox formControlName="commPrefsSms" data-test="sms-text">
          SMS text message (standard messaging rates apply)
        </iris-checkbox>
        <iris-checkbox formControlName="commPrefsPhone" data-test="phone-call">
          Phone call (they'll leave a voicemail if you aren't available)
        </iris-checkbox>
      </div>
    </iris-form-field>
  </div>
</ng-template>

<ng-template #heardFrom let-formGroup>
  <div [formGroup]="formGroup" class="heard-from-form">
    <h4 data-test="heard-from">How did you hear about us?</h4>
    <div formGroupName="heardFrom">
      <iris-dropdown
        data-test="heard-from-source"
        [options]="heardFromOptions"
        placeholder="How you heard about us"
        formControlName="source"
      >
      </iris-dropdown>

      <iris-form-field
        *ngIf="shouldShowHeardFromDetails"
        [label]="heardFromDetailsHeading" 
        class="heard-from-details-label" 
        data-test="heard-from-details-label"
      >
        <iris-text-input
          formControlName="details"
          data-test="heard-from-details"
        ></iris-text-input>
      </iris-form-field>
    </div>
  </div>
</ng-template>
